import * as React from "react";
import { Search } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Checkbox } from "./checkbox.js";

// A searchable list of checkboxes, for choosing several items at once (for
// example assigning a goal to many employees). For picking exactly one item from
// a long list, use ui/combobox instead.

export interface CheckboxListOption {
  value: string;
  label: string;
  /** Secondary text under the label; also matched by the search. */
  sub?: string | undefined;
  /** Shown checked and locked, e.g. people who already have the goal. */
  locked?: boolean | undefined;
  /** Short note shown on the right of a locked row. */
  lockedHint?: string | undefined;
}

export interface CheckboxListProps {
  options: CheckboxListOption[];
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  searchPlaceholder?: string | undefined;
  emptyText?: string | undefined;
  className?: string | undefined;
}

function CheckboxList({
  options,
  selected,
  onSelectedChange,
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  className,
}: CheckboxListProps) {
  const [query, setQuery] = React.useState("");

  const q = query.trim().toLowerCase();
  const visible = q
    ? options.filter((o) => o.label.toLowerCase().includes(q) || (o.sub ?? "").toLowerCase().includes(q))
    : options;

  // "Select all" acts on what the search is currently showing, so a user can
  // filter to a department or a name fragment and tick exactly that group.
  const selectable = visible.filter((o) => !o.locked);
  const allVisibleSelected = selectable.length > 0 && selectable.every((o) => selected.has(o.value));
  const someVisibleSelected = selectable.some((o) => selected.has(o.value));

  function toggle(value: string, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(value);
    else next.delete(value);
    onSelectedChange(next);
  }

  function toggleAllVisible(checked: boolean) {
    const next = new Set(selected);
    for (const o of selectable) {
      if (checked) next.add(o.value);
      else next.delete(o.value);
    }
    onSelectedChange(next);
  }

  return (
    <div className={cn("overflow-hidden rounded-md border border-surface-200", className)}>
      <div className="flex items-center gap-2 border-b border-surface-100 px-3">
        <Search className="h-4 w-4 shrink-0 text-surface-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-surface-400"
        />
      </div>

      {selectable.length > 0 && (
        <label className="flex cursor-pointer items-center gap-3 border-b border-surface-100 bg-surface-50 px-3 py-2 text-[12px] font-semibold text-surface-600">
          <Checkbox
            checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
            onCheckedChange={(c) => toggleAllVisible(c === true)}
          />
          {q ? `Select all ${selectable.length} shown` : `Select all (${selectable.length})`}
        </label>
      )}

      <div className="max-h-64 overflow-y-auto">
        {visible.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] text-surface-400">{emptyText}</p>
        ) : (
          visible.map((o) => (
            <label
              key={o.value}
              className={cn(
                "flex items-center gap-3 border-b border-surface-100 px-3 py-2 last:border-b-0",
                o.locked ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-surface-50"
              )}
            >
              <Checkbox
                checked={o.locked || selected.has(o.value)}
                disabled={o.locked}
                onCheckedChange={(c) => toggle(o.value, c === true)}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{o.label}</span>
                {o.sub && <span className="block truncate text-[11px] text-surface-400">{o.sub}</span>}
              </span>
              {o.locked && o.lockedHint && (
                <span className="shrink-0 text-[11px] text-surface-400">{o.lockedHint}</span>
              )}
            </label>
          ))
        )}
      </div>
    </div>
  );
}

export { CheckboxList };
